// Product catalog that the Office-365-style waffle and cross-product
// "Open in..." actions draw from. Kept as plain data so the same shape can
// later move to @athavita/ui-theme once connect-web and admin-web also ship
// the launcher.
//
// URLs are dev defaults today. Override per deployment via VITE_* env vars on
// each app (pulled below), or the `VITE_APP_DIRECTORY` JSON for an env with
// non-default ports.

export type SuiteAppId =
    | "mail"
    | "connect"
    | "admin"
    | "identity"
    | "docs"
    | "sheet"
    | "present"
    | "drive"
    | "pdfViewer";

export interface SuiteApp {
    id: SuiteAppId;
    name: string;
    blurb: string;
    url: string;
    /** Tailwind classes for the tile icon background/foreground. */
    tileClass: string;
    /** True when the product isn't shipped yet. Tile renders disabled. */
    comingSoon?: boolean;
}

function envUrl(key: string, fallback: string): string {
    const raw = (import.meta.env as Record<string, string | undefined>)[key];
    return raw && raw.trim().length > 0 ? raw : fallback;
}

export const suiteApps: SuiteApp[] = [
    {
        id: "mail",
        name: "Mail",
        blurb: "Inbox, compose, schedule",
        url: envUrl("VITE_MAIL_WEB_URL", "http://localhost:5180"),
        tileClass: "bg-blue-600 text-white"
    },
    {
        id: "connect",
        name: "Connect",
        blurb: "Chat, channels, meetings",
        url: envUrl("VITE_CONNECT_WEB_URL", "http://localhost:5200"),
        tileClass: "bg-purple-600 text-white"
    },
    {
        id: "admin",
        name: "Admin",
        blurb: "Tenants, users, policies",
        url: envUrl("VITE_ADMIN_WEB_URL", "http://localhost:5190"),
        tileClass: "bg-slate-700 text-white"
    },
    {
        id: "identity",
        name: "Account",
        blurb: "Sign-in, MFA, security",
        url: envUrl("VITE_IDENTITY_WEB_URL", "http://localhost:5002"),
        tileClass: "bg-emerald-700 text-white"
    },
    {
        id: "docs",
        name: "Docs",
        blurb: "Word-compatible editor",
        url: envUrl("VITE_DOCS_WEB_URL", "http://localhost:5210"),
        tileClass: "bg-sky-600 text-white"
    },
    {
        id: "sheet",
        name: "Sheets",
        blurb: "Excel-compatible",
        url: envUrl("VITE_SHEET_WEB_URL", "http://localhost:5220"),
        tileClass: "bg-green-700 text-white"
    },
    {
        id: "present",
        name: "Slides",
        blurb: "PowerPoint-compatible",
        url: envUrl("VITE_PRESENT_WEB_URL", "http://localhost:5232"),
        tileClass: "bg-orange-600 text-white"
    },
    {
        id: "drive",
        name: "Drive",
        blurb: "Files, folders, sharing",
        url: envUrl("VITE_DRIVE_WEB_URL", "http://localhost:5240"),
        tileClass: "bg-indigo-600 text-white"
    },
    {
        id: "pdfViewer",
        name: "PDF",
        blurb: "Annotate + sign",
        url: envUrl("VITE_PDF_WEB_URL", "http://localhost:5250"),
        tileClass: "bg-rose-600 text-white"
    }
];

export function findApp(id: SuiteAppId): SuiteApp | undefined {
    return suiteApps.find((a) => a.id === id);
}
